/**
 * <StudentLivePage> — l'élève en live : la scène ou son activité, suivant le mode (page).
 *
 * Forme B, « deux temps, suivant le mode » (Wajih, 16/10), redessinée sur wireframe le 17/10. Le
 * mode que pose le tuteur décide de ce que l'écran montre :
 *  - **Dialogue** : la scène (`<LiveStage>` : le tuteur en grand, ou son partage d'écran ; ma
 *    caméra en vignette dans son coin ; les autres élèves ne s'y montrent pas : l'en-tête dit combien sont connectés) ;
 *  - **Concentré** : l'activité (`<StudentActivityPage heading="inline">`), et Sam devant le
 *    masteur pour prendre son relais — dans la visio à l'arrivée (Wajih, 22/10), puis dans la bulle
 *    tandem sur l'activité.
 *
 * **Le tandem** (Wajih, 19/10) dit le mode : Sam devant en Concentré et pendant l'attente du
 * masteur, le masteur devant en Dialogue, et la bascule entre les deux. Jamais le masteur deux fois
 * sur le même écran : quand la visio est à l'écran, le tandem se tient dans la visio elle-même
 * (`<LiveStage tandem>`) — Sam en grand devant la vidéo réduite du masteur, ou la vidéo pleine et Sam
 * en petit ; sinon, c'est la bulle tandem (`<LiveTandemBubble>`) en bas à droite, qui porte la caméra
 * du masteur et dont le visage touché ouvre la visio. Ce que l'élève voit se décide dans une seule
 * règle, `liveSight`. Le relais est une orbite (Wajih, 20/10, `useTandemOrbit`). **La visio sort de la
 * bulle et y rentre** (Wajih, daily du 21/10, `stageMorph`) : l'orbite passe toujours d'abord, là où est
 * le tandem (dans la bulle quand la visio va sortir, dans la visio quand elle va rentrer), puis la visio
 * grandit depuis le visage de devant de la bulle jusqu'à sa place, ou y revient en rétrécissant, comme
 * aspirée. Au téléphone comme au grand écran, et pour une vue changée à la main (sans orbite : le relais
 * n'a pas changé). Les deux vues sont montées ensemble le temps de la bascule seulement.
 *
 * **Un seul en-tête** (`<LiveSessionBar>`). Au téléphone, il porte la bascule « Activité » /
 * « Visio » et la liste des activités, rien d'autre : pas de chip du mode, pas de bandeau. La vue
 * ouverte arrive en props (`view`) ; l'écran la pose sur la visio à l'entrée, puis, au téléphone, le
 * changement de mode ne la change plus (Wajih, 24/10 : l'élève quittait son exercice) — la page dit sa
 * forme (`onTabsChange`). Au grand écran (à partir de `TWO_PANE_FROM`), la visio se rouvre à chaque
 * changement de mode (Wajih, 22/10 : l'élève voit son masteur derrière Sam) ; pas de bascule ; l'en-tête
 * dit la séance et la liste :
 *  - **la visio à gauche et l'activité dans le volet de droite**, en Dialogue comme à l'arrivée en
 *    Concentré ; au web, la molette au-dessus de la visio fait défiler l'énoncé (`useWheelRelay`) ;
 *  - **en Concentré, la visio refermée** (« Revenir à l'activité », Wajih 19/10) : sa colonne repart à
 *    gauche et l'activité prend tout l'écran, l'énoncé centré sur une colonne de lecture (`liveColumns`)
 *    que la rangée de la copie suit ; la bulle tandem au coin bas droit. Le panneau de Sam s'ouvre à
 *    droite et décale l'énoncé, sans voile quand les deux tiennent côte à côte. Le visage du masteur
 *    dans la bulle rouvre la visio.
 *  L'activité y tient toujours en une colonne, celle de son volet ou celle de lecture : jamais de volet
 *  de copie à droite.
 *  La colonne de la visio s'ouvre ou se referme à la durée `standard` et à la courbe de la marque, sans
 *  être remontée, pendant que la visio sort de la bulle ou y rentre ; en mouvement réduit, elle est
 *  aussitôt à sa place.
 *  - **le partage agrandi** (Wajih, 20/10) : en Dialogue, pendant que le masteur partage son écran, un
 *    bouton de la visio l'agrandit à tout l'écran, l'énoncé masqué — l'activité reste montée derrière,
 *    elle ne perd rien, mais ne se lit ni ne se tabule plus une fois retirée. Il revient seul à la
 *    normale quand le masteur arrête de partager ; le retour arrière (Android) et Échap (web) le
 *    quittent aussi. La visio s'élargit du même mouvement que le volet se retire.
 *  - la visio repartie (le Concentré au grand écran) ne rend plus la vidéo du masteur : la bulle la
 *    porte.
 *
 * L'en-tête ne porte pas l'heure (Wajih, 20/10 : posée seule, « ça fait cheap »).
 *
 * Sur une activité, le titre et le chrono défilent avec l'énoncé ; deux pieds se suivent : la barre
 * de copie de l'activité, puis la barre du live (`<LiveControlBar>`). Au téléphone, le bouton
 * caméra de la barre EST ma vignette (`cameraPreview`) : je me vois toujours.
 *
 * Le mode se lit au téléphone par trois choses : la bulle tandem (qui est devant, le liseré du
 * masteur), le micro violet et barré (le toucher dit pourquoi, `micHint`), et ce que dit Sam au
 * passage en Concentré. En Dialogue, Sam passe derrière et se tait ; pendant l'attente du tuteur, il
 * propose de commencer l'exercice ; sans activité encore, il dit que le masteur est là (`sam.line`, la
 * page en écrit les mots). Son visage touché, devant, ouvre un checkpoint « main levée » à la
 * demande de l'élève : l'écran le pose dans `checkpointLayer`.
 *
 * Ma caméra : sur la scène, ma vignette à sa place (le coin bas droit ; Sam en petit se tient à côté du masteur) ; quand
 * la scène quitte l'écran (l'activité au téléphone, le Concentré au grand écran), elle glisse et se pose
 * en bas, dans le bouton caméra de la barre (`cameraPreview`).
 *
 * Sous l'en-tête, en barres pleine largeur : le tuteur pas encore là (retard annoncé, attente,
 * cinq minutes), la connexion faible, au téléphone sur l'activité « Ton masteur te parle · Voir la visio »
 * au retour en Dialogue (`liveTutorTalkingNotice`), « Nouvelle activité de ton masteur · Voir » quand il en
 * ajoute une sans l'afficher (la règle de temps de la précédente), et, hors de la visio, la connexion au live perdue
 * (« Reconnexion… », puis « Réessayer » quand la reprise bloque, `liveLostNotice`), qui prend la place des
 * deux précédentes ; elles s'ouvrent et se referment en glissant (`<Reveal>`). À dix minutes, la séance de
 * remplacement est une feuille (`<LiveTutorAbsentDialog>`) que l'écran pose à côté, comme « Un problème ? »
 * (`<LiveHelpSheet>`) et « Mes activités » (`<LiveActivitiesSheet>`).
 *
 * Deux choix du 18/10, chacun derrière sa prop au défaut neutre (l'écran les allume) :
 *  - `checkpointAboveControls` : la feuille de Sam d'un checkpoint et son voile s'arrêtent
 *    au-dessus de la barre du live, comme le voile de l'ancien `SamCheckpointOverlay` : le micro,
 *    la main, le chat et la sortie restent à portée pendant la conversation ;
 *  - `appBanner` : le bandeau « Installer l'appli » du web mobile (`<LiveAppBanner>`), la première
 *    des barres sous l'en-tête.
 *
 * Par-dessus : la feuille de Sam d'un checkpoint (`checkpointLayer`), puis le chat (`chatLayer`,
 * `<LiveChat>` dans `<SamSheet>`), qui passe devant elle quand il s'ouvre pendant la conversation,
 * « Veux-tu vraiment quitter ? », la séance disparue, les onglets en double, et les bandeaux
 * passagers : « Nouvelle activité affichée » quand le tuteur en pousse une, la phrase du micro
 * coupé. Ils se posent au-dessus des pieds (la barre de copie comprise), un à la fois, avec les
 * bandeaux de l'écran (`notices`) : le dernier arrivé passe devant, celui qu'il remplace est refermé
 * (`liveNoticeQueue`).
 *
 * Les vraies pistes arrivent par la fente de la scène (`stage.renderVideo`) : la page la passe à la
 * scène (sans l'écran partagé en Concentré), au visage du tuteur dans la bulle tandem et au rond de
 * ma caméra.
 *
 * Contrôlée de bout en bout. Pas de minuteur de séance : il n'existe pas aujourd'hui.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, Pressable, View, type LayoutChangeEvent } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Reveal } from "../../atoms/Reveal"
import { Text } from "../../atoms/typography/Text"
import { useHeldFor } from "../../hooks/useHeldFor"
import { usePresence } from "../../hooks/usePresence"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { orbitDurationMs } from "../../hooks/tandemOrbit"
import { useWheelRelay, WheelRelayContext } from "../../hooks/useWheelRelay"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { EmptyState } from "../../molecules/EmptyState"
import { LiveControlBar, type LiveControlBarProps } from "../../molecules/LiveControlBar"
import { LiveSessionBar, type UILiveActivitiesAccess, type UILiveSessionHeading } from "../../molecules/LiveSessionBar"
import { type UILiveView } from "../../molecules/LiveViewSwitch"
import { Snackbar, type SnackbarProps } from "../../molecules/Snackbar"
import { useAutoHide } from "../../molecules/Snackbar/useAutoHide"
import { type UISoundMode } from "../../molecules/SoundModeBadge"
import {
    LiveStage,
    stageIsSharing,
    type LiveStageProps,
    type UILiveVideoFit,
    type UILiveVideoSource,
} from "../../organisms/LiveStage"
import { LiveTandemBubble, type UITandemLead } from "../../organisms/LiveTandemBubble"
import { SamSheetBesideContext } from "../../organisms/SamSheet"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { FloatingSlot, PageTemplate } from "../../templates/PageTemplate"
import { PaneWidthContext } from "../../templates/PageTemplate/PageTemplate"
import { TWO_PANE_FROM } from "../../templates/TwoPaneTemplate/twoPaneGeometry"
import { listenEscape } from "../../utils/listenEscape"
import {
    FULL_WINDOW,
    StudentActivityPage,
    type StudentActivityPageProps,
} from "../StudentActivityPage/StudentActivityPage"
import {
    type UILiveNotice,
    type UILiveSamLine,
    type UILiveToggle,
    type UILiveWaiting,
} from "../../../ports/primary/ui/types/studentLive"
import { ActivityWidthToggle } from "./ActivityWidthToggle"
import { activityPaneWidth, readingColumnWidth, samPanelPlacement } from "./liveColumns"
import { liveLayers, SAM_SENT_NOTICE } from "./liveLayers"
import { LIVE_LOST_NOTICE_DELAY_MS, liveLostNoticeShown } from "./liveLostNotice"
import { initialLiveNoticeQueue, liveNoticeQueue, type LiveNoticeQueue } from "./liveNoticeQueue"
import { liveSight } from "./liveSight"
import { TUTOR_TALKING_NOTICE_MS, tutorTalkingNoticeDue } from "./liveTutorTalkingNotice"
import { StageMorph, type StageMorphRun, useStageMorph } from "./StageMorphLayer"
import { type ScreenBox, stageMorphFrame, type StageMorphGeometry, stageMorphGeometryFor } from "./stageMorph"

export type { UILiveView }

// L'attente du tuteur et ce qui interrompt le live : leurs formes vivent dans `ports/primary/ui/types`.
export type { UILiveWaiting, UILiveNotice }

/**
 * L'activité ouverte, telle que l'écran d'activité la lit. La page la pose sous le seul en-tête du
 * live (`heading="inline"`), sans retour, et y met Sam elle-même.
 */
export type StudentLiveActivity = Omit<
    StudentActivityPageProps,
    "heading" | "onBack" | "samTile" | "samLayer" | "testID"
>

/** Ce que la page passe à l'activité que l'écran pose (`renderActivity`). */
export type StudentLiveActivitySlots = {
    /**
     * La bulle tandem, que l'activité pose une fois au-dessus de ses étapes (`<FloatingSlot>`) : elle y
     * reste d'une étape à l'autre.
     */
    tandem?: React.ReactNode
    onFooterHeight: (height: number) => void
    /**
     * Au grand écran, la colonne où l'activité centre son énoncé et la rangée de sa copie
     * (`StudentActivityPage column`) : celle de son volet quand la visio est ouverte, la colonne de lecture
     * quand elle est refermée. Toujours une : jamais de volet de copie à droite dans le live. Absente (au
     * téléphone) : la mise en page d'avant.
     */
    column?: number
    /**
     * L'activité se voit. `false` au téléphone quand elle est posée sous la visio : ce qu'elle joue pour
     * être vu (son décompte d'entrée et son son, qui lâchent le chrono ; ses félicitations) attend qu'elle
     * s'ouvre.
     */
    onScreen: boolean
}

/**
 * Un bandeau de l'écran (`<Snackbar>`), que la page pose dans sa file : `key` le nomme dans la file,
 * `visible` dit qu'il a quelque chose à dire. La page décide quand il se montre, et où.
 */
export type UILiveScreenNotice = { key: string; visible: boolean } & Omit<
    SnackbarProps,
    "visible" | "bottomOffset" | "testID"
>

/** Sam, le tandem du tuteur. */
type StudentLiveSam = {
    /** Son visage touché, devant : l'écran ouvre un checkpoint « main levée » (`checkpointLayer`). */
    onOpen: () => void
    /** Ce que Sam dit, dans sa bulle : la page en écrit les mots. Absent ou `null` : il se tait. */
    line?: UILiveSamLine | null
    /** La croix de la bulle. */
    onDismissBubble?: () => void
    /**
     * Le masteur vient d'envoyer Sam à l'élève (sa main levée) : Sam passe devant, quel que soit le
     * mode, puis la visio s'efface ; jusqu'à ce que sa conversation se referme. Défaut `false`.
     */
    sent?: boolean
    /** La phrase qui le dit, en bandeau. Défaut `false`. */
    sentNotice?: boolean
    onDismissSentNotice?: () => void
}

export type StudentLivePageProps = {
    session: UILiveSessionHeading
    soundMode: UISoundMode
    /** Sur un téléphone, la vue ouverte. Au large, les deux se tiennent côte à côte. */
    view: UILiveView
    onChangeView: (view: UILiveView) => void
    /**
     * La page dit sa forme, une fois mesurée, puis à chaque passage du seuil : `tabs`, une vue à la fois (le
     * téléphone). L'écran y règle la vue au changement de mode.
     */
    onTabsChange?: (tabs: boolean) => void
    /**
     * Au téléphone, sur l'activité, le masteur vient de repasser en Dialogue : « Ton masteur te parle · Voir
     * la visio », dans la file des barres. Touchée, elle mène à la visio ; elle se retire seule au bout de
     * `TUTOR_TALKING_NOTICE_MS` (`onDismissTutorTalking`). Défaut `false`.
     */
    tutorTalking?: boolean
    onDismissTutorTalking?: () => void
    /**
     * Le masteur vient d'ajouter une activité sans l'afficher : « Nouvelle activité de ton masteur · Voir », dans la
     * file des barres, au ton de l'information. Touchée : `onShowAddedActivity` ; elle se retire seule au bout de
     * `TUTOR_TALKING_NOTICE_MS` (`onDismissAddedActivity`). Défaut `false`.
     */
    activityAdded?: boolean
    onShowAddedActivity?: () => void
    onDismissAddedActivity?: () => void
    /**
     * La page dit si l'activité se voit (`StudentLiveActivitySlots.onScreen`), une fois mesurée puis à chaque
     * changement : l'écran le dit au masteur.
     */
    onActivityOnScreen?: (onScreen: boolean) => void
    /** La bascule n'a pas fini à temps, et se pose d'office (`STAGE_MORPH_STUCK_MARGIN_MS`) : une trace pour l'écran. */
    onStageMorphStuck?: () => void
    /** L'activité ouverte (son id) : quand elle change, le partage agrandi se referme pour qu'elle se voie. */
    activityKey?: number | string | null
    /** La liste des activités, dans l'en-tête : elle ouvre « Mes activités ». Absente : pas de bouton. */
    activities?: UILiveActivitiesAccess

    // ── La scène ──
    /** La scène. Ma caméra s'y lit sur `controls.cameraOn`, sa seule source : `stage.me.cameraOff` est ignoré. */
    stage: LiveStageProps
    /** La connexion de l'élève est faible. Défaut `false`. */
    poorConnection?: boolean
    /** Le tuteur n'est pas là. `null` ou absent : il est là. */
    waiting?: UILiveWaiting | null

    // ── L'activité ──
    /** L'activité ouverte. Absente : plus d'activité à faire. */
    activity?: StudentLiveActivity
    /**
     * L'activité posée par l'écran lui-même (son corps entier : la page, la correction, les
     * félicitations), à la place de `activity`. Elle reçoit la tuile de Sam et le rapport de la
     * hauteur de sa barre de copie, que la page sait placer. Absente (défaut) : `activity`.
     */
    renderActivity?: (slots: StudentLiveActivitySlots) => React.ReactNode
    /**
     * Sans activité, l'élève n'en a encore eu aucune pendant ce live : la page dit « Pas encore d'activité »
     * plutôt que « Tu n'as plus d'activités ». Défaut `false`.
     */
    noActivityYet?: boolean
    /** Le tuteur vient d'en afficher une : « Nouvelle activité affichée ». Défaut `false`. */
    newActivity?: boolean
    onDismissNewActivity?: () => void
    /**
     * Au grand écran, la visio ouverte : l'activité agrandie (`on`, `liveColumns.activityPaneWidth`) et le
     * bouton rond sur la séparation qui la bascule. L'écran garde le choix sur l'appareil. Absent (défaut) :
     * ni bouton, ni activité agrandie.
     */
    activityEnlarge?: UILiveToggle
    /**
     * Sam, dans la bulle tandem : devant en Concentré et pendant l'attente, derrière en Dialogue.
     * Absent : le masteur seul dans la bulle.
     */
    sam?: StudentLiveSam

    // ── La barre ──
    controls: Omit<LiveControlBarProps, "soundMode" | "cameraPreview" | "cameraPreviewVideo">
    /** En Concentré, le micro touché : la phrase qui dit pourquoi il ne s'ouvre pas. Défaut `false`. */
    micHint?: boolean
    onDismissMicHint?: () => void
    /** « Veux-tu vraiment quitter ? » est posée. Défaut `false`. */
    confirmLeave?: boolean
    onConfirmLeave?: () => void
    onCancelLeave?: () => void
    /** Le voile ou le retour d'Android referment « Veux-tu vraiment quitter ? ». Absent : `onCancelLeave`. */
    onDismissLeave?: () => void
    /**
     * L'écran pose une feuille de la sortie (la note, la sortie qui part) : le chat et Sam se retirent
     * derrière elle (`liveLayers`). Défaut `false`.
     */
    leaveSheet?: boolean
    /**
     * Les élèves connectés au live, moi compris : l'en-tête du grand écran dit les autres (« Un autre élève
     * connecté »), et rien quand je suis seul. Absent : rien.
     */
    connectedStudents?: number

    // ── Ce qui se pose par-dessus ──
    /** Le chat ouvert : `<SamSheet>` qui porte `<LiveChat>`. */
    chatLayer?: React.ReactNode
    /** La conversation de Sam d'un checkpoint : `<SamSheet>` qui porte sa conversation. */
    checkpointLayer?: React.ReactNode
    /**
     * La feuille du checkpoint s'arrête au-dessus de la barre du live, qui reste à portée. Défaut
     * `false` : elle couvre l'écran entier.
     */
    checkpointAboveControls?: boolean
    /**
     * Les bandeaux de l'écran, posés dans la file de ceux de la page : un à la fois, le dernier arrivé
     * devant, au-dessus de la barre du live. Le remplacé est refermé (son `onDismiss`). Absent : aucun.
     */
    notices?: UILiveScreenNotice[]
    /** Le bandeau « Installer l'appli » (`<LiveAppBanner>`), sous l'en-tête. Absent : pas de bandeau. */
    appBanner?: React.ReactNode
    notice?: UILiveNotice | null
    /** Le message du serveur sous un accès suspendu ou un live d'un autre jour ; la page a le sien sinon. */
    noticeMessage?: string | null
    /** « OK » sous la séance disparue, l'accès suspendu ou le live d'un autre jour : la sortie. */
    onCloseNotice?: () => void
    /** « Garder cet onglet ». */
    onKeepTab?: () => void
    testID?: string
}

/** Stable d'un rendu à l'autre : une fonction neuve relancerait le minuteur de retrait du `<Snackbar>`. */
const NOOP = () => {}

/** Aucun bandeau de l'écran : stable d'un rendu à l'autre. */
const NO_NOTICES: UILiveScreenNotice[] = []

/** Le prénom du masteur, ou « Ton masteur » sans prénom. */
function tutorOrDefault(tutor: string): string {
    const name = tutor.trim()
    return name !== "" ? name : "Ton masteur"
}

const TEXTS = {
    noActivityTitle: "Tu n'as plus d'activités",
    noActivityMessage: "Ton masteur va t'en ajouter sous peu.",
    // Wajih, 22/10 : à l'arrivée, « plus » est faux, et on ne promet rien.
    noActivityYetTitle: "Pas encore d'activité",
    newActivity: "Nouvelle activité affichée",
    micHint: "Micro coupé par ton masteur. Lève la main.",
    poorConnection: "Connexion faible",
    poorConnectionDetail: "Rapproche-toi de ta box Wi-Fi, ou ferme les autres applis.",
    connectionLost: "Connexion au live perdue · Reconnexion…",
    liveUnreachable: "Le live n'est plus joignable",
    retryConnection: "Réessayer",
    tutorTalking: "Ton masteur te parle · Voir la visio",
    activityAdded: "Nouvelle activité de ton masteur · Voir",
    samFocused: (tutor: string) => `${tutor} aide un autre élève. Je suis là si tu bloques.`,
    samWaiting: "On commence l'exercice ensemble ?",
    // Wajih, 22/10 : on ne sait pas si le masteur prépare une activité, la phrase ne promet rien de ce
    // qu'il fait.
    samFirstActivity: (tutor: string) => `${tutorOrDefault(tutor)} est là et va bientôt pouvoir discuter avec toi.`,
    samSent: SAM_SENT_NOTICE,
    showDetail: "Voir le détail",
    leaveTitle: "Veux-tu vraiment quitter ?",
    leaveConfirm: "Quitter le live",
    leaveCancel: "Annuler",
    goneTitle: "Cette session n'est plus disponible",
    goneMessage: "Tu as été déconnecté du live.",
    goneConfirm: "OK",
    paymentTitle: "Ton accès au live est suspendu",
    paymentMessage: "Un paiement est en attente. Régularise ton abonnement pour retrouver l'accès.",
    forbiddenTitle: "Ce live n'est pas ouvert aujourd'hui",
    forbiddenMessage: "Ce live est accessible uniquement le jour-même.",
    tabsTitle: "Plusieurs onglets détectés",
    tabsMessage: "Ce live est ouvert dans un autre onglet. Garde celui-ci : les autres se fermeront.",
    tabsConfirm: "Garder cet onglet",
}

/**
 * Les mots de l'attente : une ligne courte, que l'élève lit d'un coup d'œil (le prénom de son
 * masteur, et l'heure qu'il a annoncée), et le détail, qui s'ouvre au toucher de la barre.
 */
function liveWaitingWords(
    waiting: UILiveWaiting,
    hasActivity: boolean,
    tutor: string
): { title: string; message: string } {
    const name = tutorOrDefault(tutor)
    const meanwhile = hasActivity ? "En attendant, avance ton activité." : "En attendant, commence tes devoirs."

    if (waiting.reason === "early") {
        return {
            title: waiting.late === true ? "La séance commence bientôt" : "Tu es en avance",
            message: `${name} arrive à l'heure du début. ${meanwhile}`,
        }
    }
    if (waiting.arrival != null && waiting.reason !== "busy") {
        return { title: `${name} arrive · vers ${waiting.arrival}`, message: `${name} a prévenu. ${meanwhile}` }
    }
    if (waiting.reason === "busy") {
        return { title: `${name} finit un autre cours`, message: `${name} arrive. ${meanwhile}` }
    }
    return {
        title: waiting.late === true ? `${name} arrive bientôt` : `${name} n'est pas encore là`,
        message: `${name} ne devrait pas tarder. ${meanwhile}`,
    }
}

/** Les mots de Sam, pour ce qu'il a à dire. */
function samWords(line: UILiveSamLine, tutor: string): string {
    if (line === "waiting") return TEXTS.samWaiting
    if (line === "first_activity") return TEXTS.samFirstActivity(tutor)
    return TEXTS.samFocused(tutor)
}

/** Une barre pleine largeur sous l'en-tête : sa tinte et l'encre qui s'y pose. */
type NoticeTone = { tint: SemanticColorKey; ink: SemanticColorKey }
const INFO: NoticeTone = { tint: "infoLight", ink: "info" }
const ALERT: NoticeTone = { tint: "warningLight", ink: "warning-strong" }
type NoticeIcon = "clock" | "wifi" | "sync-alt" | "video" | "tasks"

/**
 * Le volet de l'activité, au large, qui se mesure : ce qui s'y pose (le quiz, une activité faite, la
 * correction) s'y borne (`PaneWidthContext`), sa propre borne étant souvent plus large que le volet. La
 * mesure vit ici, pas dans la page : pendant que le volet glisse, elle change à chaque image, et seul ce qui
 * la lit se rend de nouveau. `cap` : la largeur du volet posé — agrandi, ce qu'il porte ne s'élargit qu'à la
 * fin du glissement, comme la colonne de l'énoncé, au lieu de se remettre en page à chaque image.
 */
function MeasuredPane(props: { cap?: number; children: React.ReactNode }) {
    const { cap, children } = props
    // État d'interface : la largeur mesurée du volet.
    const [width, setWidth] = useState<number | null>(null)
    const bound = width != null && cap != null ? Math.min(width, cap) : width
    return (
        <View style={styles.paneFill} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
            <PaneWidthContext.Provider value={bound}>{children}</PaneWidthContext.Provider>
        </View>
    )
}

/**
 * Une barre, tenue ouverte jusqu'à la fin de la bascule de la visio (`run` : elle sort de la bulle ou y rentre)
 * commencée au moment où elle se referme ; ensuite, ou sans bascule, elle suit `open`.
 */
function useOpenThroughMorph(open: boolean, run: StageMorphRun | null): boolean {
    // État d'interface : `open` d'avant, et la bascule de la visio que la barre attend.
    const [was, setWas] = useState(open)
    const [heldFor, setHeldFor] = useState<StageMorphRun | null>(null)
    if (was !== open) {
        setWas(open)
        setHeldFor(!open && run?.plan.morph != null ? run : null)
    }
    return open || (heldFor != null && heldFor === run)
}

/**
 * Ce que la page dit à l'écran (sa forme, l'activité qui se voit) : une fois mesurée — avant, elle se croit étroite
 * (`useWidthAtLeast`) —, puis à chaque changement.
 */
function useReportOnceLaidOut<T>(laidOut: boolean, value: T, report: ((value: T) => void) | undefined) {
    const reportRef = useRef(report)
    reportRef.current = report
    useEffect(() => {
        if (laidOut) reportRef.current?.(value)
    }, [laidOut, value])
}

export function StudentLivePage(props: StudentLivePageProps) {
    const {
        session,
        soundMode,
        view,
        onChangeView,
        onTabsChange,
        tutorTalking = false,
        onDismissTutorTalking,
        activityAdded = false,
        onShowAddedActivity,
        onDismissAddedActivity,
        onActivityOnScreen,
        onStageMorphStuck,
        activityKey,
        activities,
        stage,
        poorConnection = false,
        waiting = null,
        activity,
        renderActivity,
        noActivityYet = false,
        newActivity = false,
        onDismissNewActivity,
        activityEnlarge,
        sam,
        controls,
        micHint = false,
        onDismissMicHint,
        confirmLeave = false,
        onConfirmLeave,
        onCancelLeave,
        onDismissLeave,
        leaveSheet = false,
        connectedStudents,
        chatLayer,
        checkpointLayer,
        checkpointAboveControls = false,
        notices = NO_NOTICES,
        appBanner,
        notice = null,
        noticeMessage = null,
        onCloseNotice,
        onKeepTab,
        testID,
    } = props

    // État d'interface : la largeur de l'écran dit la forme de l'en-tête, comme celle des volets.
    const { onLayout: onWideLayout, atLeast: wide } = useWidthAtLeast(TWO_PANE_FROM)
    // État d'interface : la largeur de l'écran, au pixel : la colonne de la visio et la place du panneau
    // de Sam s'y mesurent.
    const [width, setWidth] = useState<number | null>(null)
    const onLayout = (event: LayoutChangeEvent) => {
        onWideLayout(event)
        setWidth(event.nativeEvent.layout.width)
    }

    // État d'interface : la hauteur des deux pieds, mesurée. Les bandeaux passagers se posent
    // au-dessus de la barre du live et, quand l'activité est à l'écran, de sa barre de copie.
    const [controlsHeight, setControlsHeight] = useState(0)
    const [copyBarHeight, setCopyBarHeight] = useState(0)

    // Un bandeau à la fois, ceux de la page et ceux de l'écran : le dernier arrivé passe devant, le
    // remplacé ne revient pas. Arrivés ensemble, Sam envoyé passe d'abord.
    const samSent = sam?.sent === true
    const allNotices: UILiveScreenNotice[] = [
        {
            key: "page:sam",
            visible: sam?.sentNotice === true,
            message: TEXTS.samSent,
            tone: "info",
            onDismiss: sam?.onDismissSentNotice ?? NOOP,
        },
        {
            key: "page:activity",
            visible: newActivity,
            message: TEXTS.newActivity,
            tone: "info",
            onDismiss: onDismissNewActivity ?? NOOP,
        },
        {
            key: "page:mic",
            visible: micHint,
            message: TEXTS.micHint,
            tone: "info",
            iconName: "microphone-slash",
            onDismiss: onDismissMicHint ?? NOOP,
        },
        ...notices,
    ]
    // État d'interface : la file. Elle se relit pendant le rendu (l'état dérivé de React), sans un rendu
    // de retard sur le bandeau qui arrive.
    const [noticeQueue, setNoticeQueue] = useState<LiveNoticeQueue>(initialLiveNoticeQueue)
    const nextQueue = liveNoticeQueue(
        noticeQueue,
        allNotices.filter((notice) => notice.visible).map((notice) => notice.key)
    )
    if (nextQueue !== noticeQueue) setNoticeQueue(nextQueue)
    const front = nextQueue.front
    // Le remplacé est refermé, une fois : son écran l'oublie, et il ne se remontre pas.
    const supersededKey = nextQueue.superseded.join("|")
    const noticesRef = useRef(allNotices)
    noticesRef.current = allNotices
    const closedRef = useRef<Set<string>>(new Set())
    useEffect(() => {
        const superseded = new Set(supersededKey === "" ? [] : supersededKey.split("|"))
        for (const key of Array.from(closedRef.current)) if (!superseded.has(key)) closedRef.current.delete(key)
        for (const notice of noticesRef.current) {
            if (!superseded.has(notice.key) || closedRef.current.has(notice.key)) continue
            closedRef.current.add(notice.key)
            notice.onDismiss()
        }
    }, [supersededKey])

    const focused = soundMode === "focused"
    const hasActivity = activity != null || renderActivity != null
    const words = waiting != null ? liveWaitingWords(waiting, hasActivity, session.tutor.firstname) : null
    // État d'interface : la barre touchée dit son détail.
    const [openBar, setOpenBar] = useState<"waiting" | "connection" | null>(null)
    // Une couche à la fois, et la visio sous le voile quand un volet s'ouvre (`liveLayers`).
    const layers = liveLayers({
        chat: chatLayer != null,
        checkpoint: checkpointLayer != null,
        leave: confirmLeave || leaveSheet,
        samSent,
    })

    // Le tandem : Sam devant en Concentré, quand le tuteur n'est pas encore arrivé, et quand le tuteur
    // l'envoie à l'élève ; en Dialogue, le tuteur passe devant, Sam se tait. Il se tient dans la visio
    // quand elle est à l'écran ; la bulle ne se pose que sur l'activité d'un téléphone, où le visage du
    // tuteur porte sa caméra et mène à la visio.
    const lead: UITandemLead = focused || waiting != null || samSent ? "sam" : "tutor"
    // État d'interface : l'élève a agrandi le partage d'écran. Il retombe dès que le partage s'arrête, ou
    // ne se montre plus (le Concentré).
    const sharing = !focused && stageIsSharing(stage)
    const [shareFullAsked, setShareFullAsked] = useState(false)
    useEffect(() => {
        if (!sharing) setShareFullAsked(false)
    }, [sharing])
    // Une autre activité ouverte (choisie, affichée par le masteur) se montre : le partage agrandi retire son volet.
    useEffect(() => setShareFullAsked(false), [activityKey])
    // Le masteur affiche l'activité déjà ouverte : sa clé ne change pas, et le partage agrandi la cacherait encore.
    useEffect(() => {
        if (newActivity) setShareFullAsked(false)
    }, [newActivity])
    const sight = liveSight(soundMode, wide, view, shareFullAsked && sharing)
    // Envoyé par le tuteur, Sam passe devant, puis la visio s'efface : le voile attend la fin de l'orbite.
    const veil = useVeil(layers.stageVeil, samSent && checkpointLayer == null && chatLayer == null, sight.stage)
    // La connexion au live perdue, dite hors de la visio, une fois la micro-coupure passée (`liveLostNotice`).
    const lostLasting = useHeldFor(stage.connection === "reconnecting", LIVE_LOST_NOTICE_DELAY_MS)
    const lost = liveLostNoticeShown({
        connection: stage.connection ?? null,
        lasting: lostLasting,
        canRetry: stage.onRetryConnection != null,
        stageOnScreen: sight.stage,
    })
    const talkingDue = tutorTalkingNoticeDue({ tutorTalking, wide, view })
    useAutoHide(talkingDue, TUTOR_TALKING_NOTICE_MS, onDismissTutorTalking ?? NOOP)
    const laidOut = width != null
    useReportOnceLaidOut(laidOut, !wide, onTabsChange)
    // La visio sort de la bulle ou y rentre, l'orbite d'abord (`stageMorph`). La page mesure la zone où se
    // tiennent la visio et l'activité, et le visage de devant de la bulle : la visio ne bouge qu'une fois
    // sa géométrie connue.
    const reduceMotion = useReduceMotion()
    const regionRef = useRef<View>(null)
    const bubbleFrontRef = useRef<View>(null)
    const stageHostRef = useRef<View>(null)
    const [measured, setMeasured] = useState<{ progress: Animated.Value; geometry: StageMorphGeometry } | null>(null)
    const samCanLead = sam != null
    const morph = useStageMorph(
        { stage: sight.stage, lead },
        !reduceMotion,
        measured?.progress ?? null,
        onStageMorphStuck
    )
    const run = morph.run
    const frame = stageMorphFrame(run, morph.settled)
    // Les barres qui partent avec la visio qui sort de la bulle ou y rentre (« Ton masteur te parle » touchée, la
    // connexion perdue sur « Visio », « Nouvelle activité » touchée sur « Visio ») attendent qu'elle soit posée : la
    // zone s'est mesurée barres ouvertes, la visio aurait fini à côté de sa place, puis sauté. Posée, elle suit leur
    // repli image par image.
    const talkingOpen = useOpenThroughMorph(talkingDue && !lost, run)
    const lostOpen = useOpenThroughMorph(lost, run)
    // La règle de « Ton masteur te parle » : la barre invite, son geste reste dans « Mes activités ».
    useAutoHide(activityAdded, TUTOR_TALKING_NOTICE_MS, onDismissAddedActivity ?? NOOP)
    const addedOpen = useOpenThroughMorph(activityAdded, run)
    // Le volet de l'activité, la visio ouverte : à sa taille, ou agrandi par le bouton sur la séparation.
    const paneWidth = activityPaneWidth(wide ? width : null, activityEnlarge?.on === true)
    const stageWidth = wide && width != null ? Math.max(0, width - paneWidth) : null
    useEffect(() => {
        if (run == null || run.phase !== "morph") return undefined
        let gone = false
        Promise.all([measureBox(regionRef.current), measureBox(bubbleFrontRef.current)]).then(([region, front]) => {
            if (gone) return
            const box = region ?? { x: 0, y: 0, width: width ?? 0, height: 0 }
            setMeasured({
                progress: run.progress,
                geometry: stageMorphGeometryFor({
                    region: box,
                    front,
                    stageWidth: stageWidth ?? box.width,
                    samLeads: samCanLead && run.to.lead === "sam",
                    twins: (stage.twins?.length ?? 0) > 0,
                }),
            })
        })
        return () => {
            gone = true
        }
        // La mesure se prend au début du mouvement, une fois.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run])
    const geometry = run != null && measured?.progress === run.progress ? measured.geometry : null
    // La bulle s'efface sous la visio qui sort d'elle une fois la visio posée sur elle, pas avant.
    const bubbleHidden = frame.bubbleHidden && !(run?.plan.morph === "grow" && geometry == null)
    // Android : la vidéo (une `SurfaceView`) ne suit pas la visio qui sort ou rentre, la photo du masteur le
    // temps du mouvement.
    const videoStill = frame.morph != null && Platform.OS === "android"
    // Tant que la visio n'a pas commencé à rentrer, elle couvre encore l'activité : son orbite, puis sa mesure.
    const activityUnseen = run?.plan.morph === "shrink" && (run.phase === "orbit" || geometry == null)
    // Le partage agrandi se quitte comme une feuille : le retour arrière (Android), Échap (web).
    useEscape(sight.shareFull, () => setShareFullAsked(false))
    const samInTandem =
        sam != null
            ? {
                  onOpen: sam.onOpen,
                  peek: sam.line != null ? samWords(sam.line, session.tutor.firstname) : null,
                  onDismissPeek: sam.onDismissBubble,
              }
            : undefined
    // La bulle est posée dès que la visio va y rentrer (elle se mesure, cachée), et cachée quand la visio
    // sort d'elle.
    const bubble = frame.bubble ? (
        <LiveTandemBubble
            lead={lead}
            hidden={bubbleHidden}
            frontRef={bubbleFrontRef}
            soundMode={soundMode}
            tutor={stage.tutor}
            tutorName={session.tutor.firstname}
            tutorVideo={videoStill ? undefined : (stage.renderVideo?.("tutor") ?? undefined)}
            tutorAbsent={stage.tutorAbsent}
            onPressTutor={() => onChangeView("stage")}
            sam={samInTandem}
        />
    ) : undefined

    // Ma caméra n'a qu'une source, le bouton de la barre : ma vignette et le rond de la barre la suivent,
    // ils ne peuvent pas dire autre chose que lui.
    const me = { ...stage.me, cameraOff: !controls.cameraOn }
    // Ma caméra va dans la barre dès que la visio part : elle n'y est plus, ou rentre dans la bulle.
    const cameraInBar = !frame.stage || frame.morph === "shrink"
    // La colonne de la visio, au large : elle va et vient depuis la gauche ; l'activité prend la place
    // qu'elle laisse. Repartie (le Concentré), elle ne rend plus la vidéo du masteur : la bulle la porte.
    // En mouvement réduit, ou au téléphone (une vue à la fois), elle est aussitôt à sa place.
    const stageIn = usePresence(frame.column, wide)
    const stageRest = useMemo(
        () => stageIn.progress.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }),
        [stageIn.progress]
    )
    // Au grand écran, la colonne refermée sort de la mise en page, sauf pendant que la visio rentre dans
    // la bulle : elle déborde alors de sa colonne.
    const stageHidden = wide && !stageIn.mounted && !frame.stage
    // En Concentré, le partage d'écran du tuteur ne se montre pas, comme aujourd'hui : ni son image, ni sa
    // piste. La visio qui rentre dans la bulle garde le sien jusqu'au bout.
    const stageShowsScreen = sight.tutorScreen || run?.plan.morph === "shrink"
    const renderVideo = stage.renderVideo
    const stageVideo =
        videoStill || stageHidden
            ? () => null
            : !stageShowsScreen && renderVideo != null
              ? (source: UILiveVideoSource, fit?: UILiveVideoFit) =>
                    source === "screen" ? null : renderVideo(source, fit)
              : renderVideo
    const stageNode = (
        <View style={styles.stageHost} ref={stageHostRef}>
            <LiveStage
                {...stage}
                me={me}
                focused={focused}
                screenUri={stageShowsScreen ? stage.screenUri : undefined}
                renderVideo={stageVideo}
                // La visio qui va rentrer dans la bulle fait d'abord son orbite.
                tandem={{ lead, sam: samInTandem }}
                shareFullScreen={
                    sight.shareToggle && sharing
                        ? { on: sight.shareFull, onToggle: () => setShareFullAsked((asked) => !asked) }
                        : undefined
                }
                // La visio rouverte en Concentré au grand écran se referme ici : il n'y a pas de bascule.
                onHide={wide && focused ? () => onChangeView("activity") : undefined}
            />
            {/* Le voile des feuilles sur la visio, quand un volet s'ouvre par-dessus (`liveLayers`). */}
            <Animated.View style={[styles.stageVeil, { opacity: veil }]} pointerEvents="none" />
        </View>
    )
    // L'activité prend tout l'écran, en colonne de lecture, une fois la colonne de la visio refermée.
    const readingColumn = wide && !frame.column ? readingColumnWidth() : undefined
    // Visio ouverte, l'activité tient dans la colonne de son volet, jamais sans colonne (sans elle, le volet
    // encore large le temps que la visio va et vient y ouvrait un volet de copie, et la bulle tombait). La
    // colonne suit le volet posé : agrandi, à la fin de son glissement ; réduit, sur-le-champ. L'énoncé se remet
    // en page une fois, pas à chaque image (sa figure y aurait grandi puis sauté, `figureMaxWidth`).
    const [settledPaneWidth, setSettledPaneWidth] = useState(paneWidth)
    if (paneWidth < settledPaneWidth) setSettledPaneWidth(paneWidth)
    const column = wide ? (readingColumn ?? Math.min(paneWidth, settledPaneWidth)) : undefined
    // Au téléphone, l'activité montée sous la visio ne se voit ni pendant l'orbite de la visio qui va rentrer
    // dans la bulle, ni dès que la visio en sort. Au large, son volet retiré par le partage agrandi non plus. Sa
    // barre de copie ne compte sous les bandeaux que quand elle se voit.
    const visioComing = run?.plan.morph === "grow" && run.phase === "morph" && geometry != null
    const activityOnScreen = wide ? !sight.shareFull : frame.bubble && !activityUnseen && !visioComing
    const aboveFooters = controlsHeight + (activityOnScreen ? copyBarHeight : 0)
    useReportOnceLaidOut(laidOut, activityOnScreen, onActivityOnScreen)
    // Au grand écran, la molette au-dessus de la visio fait défiler l'énoncé d'à côté (`useWheelRelay`).
    const registerWheel = useWheelRelay(stageHostRef, wide && sight.stage && !sight.shareFull)
    const activityNode =
        renderActivity != null ? (
            renderActivity({
                tandem: bubble,
                onFooterHeight: setCopyBarHeight,
                column,
                onScreen: activityOnScreen,
            })
        ) : activity != null ? (
            <FloatingSlot floating={bubble}>
                <StudentActivityPage {...activity} heading="inline" column={column} onFooterHeight={setCopyBarHeight} />
            </FloatingSlot>
        ) : (
            // Sans activité, Sam et le masteur restent là, la bulle de Sam comprise (Wajih, 22/10).
            <FloatingSlot floating={bubble}>
                <View style={styles.empty}>
                    {noActivityYet ? (
                        <EmptyState title={TEXTS.noActivityYetTitle} iconName="tasks" />
                    ) : (
                        <EmptyState title={TEXTS.noActivityTitle} message={TEXTS.noActivityMessage} iconName="tasks" />
                    )}
                </View>
            </FloatingSlot>
        )

    // Le panneau de Sam ouvert à côté de la colonne de lecture décale l'activité.
    // Le partage agrandi : le volet de l'activité se retire à droite, sans être démonté ; retiré, il ne
    // se lit ni ne se tabule plus (`activityGone`).
    const [activityGone, setActivityGone] = useState(false)
    // Agrandie ou rendue à son volet, l'activité glisse du même mouvement — sous le geste seulement : la largeur
    // gardée sur l'appareil, lue à l'entrée dans le live (`activityEnlarge` qui arrive), se pose d'emblée.
    const activityBasis = useEased(
        sight.shareFull ? 0 : paneWidth,
        wide,
        (to) => {
            setActivityGone(to === 0)
            if (to > 0) setSettledPaneWidth(to)
        },
        activityEnlarge != null
    )
    useEffect(() => {
        if (!sight.shareFull) setActivityGone(false)
    }, [sight.shareFull])
    // La visio suit le volet du même mouvement : sa largeur est celle que le volet lui laisse, à chaque image.
    const stageSlide = useMemo(() => {
        if (width == null || width <= 0) return null
        return {
            width: activityBasis.interpolate({
                inputRange: [0, width],
                outputRange: [width, 0],
                extrapolate: "clamp",
            }),
        }
    }, [width, activityBasis])
    // Le bouton rond sur la séparation : visio ouverte à côté de l'activité, hors du partage agrandi (qui ne
    // change pas) et du mouvement de la visio qui sort de la bulle ou y rentre.
    const enlargeToggle =
        activityEnlarge != null && wide && frame.column && frame.morph == null && !sight.shareFull ? (
            <ActivityWidthToggle {...activityEnlarge} />
        ) : null
    const panel = width != null && readingColumn != null ? samPanelPlacement(width) : null
    const besidePanel = panel?.beside === true
    const shift = useEased(checkpointLayer != null && besidePanel ? (panel?.shift ?? 0) : 0, wide)

    const header = (
        <LiveSessionBar
            session={session}
            activities={activities}
            connectedStudents={connectedStudents}
            viewSwitch={wide ? undefined : { view, onChange: onChangeView }}
        />
    )

    // La ligne d'une barre : son icône et son titre, puis ce qui se tient au bout.
    const noticeLine = (tone: NoticeTone, icon: NoticeIcon, title: string, end: React.ReactNode) => (
        <Row gap="2" align="center">
            <View style={styles.noticeText}>
                <Banner icon={icon} color={tone.ink} align="start" message={title} />
            </View>
            {end}
        </Row>
    )
    // Une barre d'information qui mène quelque part : toute la barre est le bouton.
    const noticeLink = (icon: NoticeIcon, title: string, onPress: () => void) => (
        <Pressable
            style={styles.noticeBar(INFO.tint)}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={title}
        >
            {noticeLine(INFO, icon, title, <Icon name="chevron-right" size={14} color={INFO.ink} />)}
        </Pressable>
    )
    // Une ligne courte ; toucher la barre ouvre (ou referme) son détail.
    const noticeBar = (
        key: "waiting" | "connection",
        tone: NoticeTone,
        icon: NoticeIcon,
        title: string,
        detail: string
    ) => {
        const open = openBar === key
        return (
            <Pressable
                style={styles.noticeBar(tone.tint)}
                onPress={() => setOpenBar(open ? null : key)}
                accessibilityRole="button"
                accessibilityState={{ expanded: open }}
                aria-expanded={open}
                accessibilityHint={open ? undefined : TEXTS.showDetail}
            >
                {noticeLine(
                    tone,
                    icon,
                    title,
                    <Icon name={open ? "chevron-up" : "chevron-down"} size={14} color={tone.ink} />
                )}
                {/* Le détail s'ouvre en douceur : la visio et l'activité glissent dessous au lieu de sauter. */}
                <Reveal open={open}>
                    <Text size="text-sm" weight="regular" color={tone.ink} style={styles.noticeDetail}>
                        {detail}
                    </Text>
                </Reveal>
            </Pressable>
        )
    }

    return (
        <View style={styles.root} testID={testID} onLayout={onLayout}>
            <PageTemplate
                header={header}
                scroll={false}
                padding="0"
                maxContentWidth={FULL_WINDOW}
                contentContainerStyle={styles.flush}
                onFooterLayout={setControlsHeight}
                footer={
                    <LiveControlBar
                        {...controls}
                        soundMode={soundMode}
                        voiceId={me.id}
                        cameraPreview={cameraInBar ? me : undefined}
                        cameraPreviewVideo={cameraInBar ? renderVideo?.("me") : undefined}
                    />
                }
            >
                {appBanner}
                {words != null &&
                    noticeBar("waiting", waiting?.late === true ? ALERT : INFO, "clock", words.title, words.message)}
                {/* La connexion faible cède sa place à la connexion perdue, qui la dit déjà ; l'une et l'autre
                    glissent, la visio et l'activité avec elles. */}
                <Reveal open={poorConnection && !lostOpen}>
                    {noticeBar("connection", ALERT, "wifi", TEXTS.poorConnection, TEXTS.poorConnectionDetail)}
                </Reveal>
                {/* La région vive reste montée : une barre insérée déjà remplie ne s'annonce pas toujours. */}
                <View aria-live="polite">
                    <Reveal open={talkingOpen}>
                        {noticeLink("video", TEXTS.tutorTalking, () => onChangeView("stage"))}
                    </Reveal>
                    <Reveal open={addedOpen}>
                        {noticeLink("tasks", TEXTS.activityAdded, onShowAddedActivity ?? NOOP)}
                    </Reveal>
                    <Reveal open={lostOpen}>
                        <View style={styles.noticeBar(ALERT.tint)}>
                            {noticeLine(
                                ALERT,
                                "sync-alt",
                                stage.connection === "unreachable" ? TEXTS.liveUnreachable : TEXTS.connectionLost,
                                stage.onRetryConnection != null && (
                                    <TextButton
                                        label={TEXTS.retryConnection}
                                        color={ALERT.ink}
                                        size="sm"
                                        onPress={stage.onRetryConnection}
                                    />
                                )
                            )}
                        </View>
                    </Reveal>
                </View>
                {wide ? (
                    // Au large, la visio à gauche et l'activité à droite, chacune dans SON volet. En
                    // Concentré, la visio repart à gauche sans quitter l'arbre : son retour ne la remonte
                    // jamais (un nœud qui change de place, React le remonte). L'activité, à la largeur
                    // d'un volet en Dialogue, grandit d'autant que la visio se retire.
                    <View style={styles.panes} ref={regionRef}>
                        <Animated.View
                            style={[
                                styles.stagePane,
                                { flexGrow: stageIn.progress },
                                frame.morph != null && styles.morphing,
                                stageHidden && styles.gone,
                            ]}
                            pointerEvents={sight.stage ? "auto" : "none"}
                            aria-hidden={!sight.stage}
                            importantForAccessibility={sight.stage ? "auto" : "no-hide-descendants"}
                        >
                            <Animated.View style={[styles.stageSlide, stageSlide]}>
                                <StageMorph run={run} geometry={geometry}>
                                    {stageNode}
                                </StageMorph>
                            </Animated.View>
                        </Animated.View>
                        <Animated.View
                            style={[
                                styles.activityPane,
                                { flexGrow: stageRest, width: activityBasis, paddingRight: shift },
                                frame.column && !sight.shareFull && styles.paneDivider,
                                sight.shareFull && styles.clip,
                                activityGone && styles.unreachable,
                            ]}
                            aria-hidden={sight.shareFull}
                            importantForAccessibility={sight.shareFull ? "no-hide-descendants" : "auto"}
                        >
                            <MeasuredPane cap={readingColumn == null ? settledPaneWidth : undefined}>
                                <WheelRelayContext.Provider value={registerWheel}>
                                    {activityNode}
                                </WheelRelayContext.Provider>
                            </MeasuredPane>
                            {enlargeToggle}
                        </Animated.View>
                    </View>
                ) : (
                    // Au téléphone, une vue à la fois ; les deux ensemble le temps de la bascule, chacune à sa
                    // place dans l'arbre : celle qui part n'est pas remontée. La visio, posée après, passe
                    // devant l'activité quand elle sort de la bulle ou y rentre.
                    <View style={styles.onePane} ref={regionRef}>
                        {/* L'activité reste montée sous la visio, cachée : la bulle qui paraît avec la bascule se
                            pose d'emblée au-dessus de sa barre de copie, déjà mesurée. Montée avec la bascule, la
                            bulle paraissait d'abord sous la barre, le temps de la mesurer, et la visio visait là.
                            Elle ne se voit pas non plus pendant l'orbite de la visio qui va rentrer dans la bulle :
                            Sam et le masteur s'y tiennent sur le fond de la page. */}
                        <View
                            style={[styles.view, !activityOnScreen && [styles.unseen, styles.unreachable]]}
                            pointerEvents={view === "activity" ? "auto" : "none"}
                            aria-hidden={view !== "activity"}
                            importantForAccessibility={view === "activity" ? "auto" : "no-hide-descendants"}
                        >
                            {activityNode}
                        </View>
                        {frame.stage && (
                            <View
                                style={styles.view}
                                pointerEvents={view === "stage" ? "box-none" : "none"}
                                aria-hidden={view !== "stage"}
                                importantForAccessibility={view === "stage" ? "auto" : "no-hide-descendants"}
                            >
                                <StageMorph run={run} geometry={geometry}>
                                    {stageNode}
                                </StageMorph>
                            </View>
                        )}
                    </View>
                )}
            </PageTemplate>

            {/* Pendant la sortie, le chat et Sam se retirent derrière elle, sans être démontés. */}
            <View style={[styles.layers, !layers.sheets && styles.gone]} pointerEvents="box-none">
                {/* Le panneau de Sam à côté de la colonne de lecture : sans voile, l'activité décalée. */}
                <SamSheetBesideContext.Provider value={besidePanel}>
                    {checkpointAboveControls && checkpointLayer != null ? (
                        // La feuille remplit son conteneur : il s'arrête où commence la barre du live.
                        <View style={styles.aboveControls(controlsHeight)} pointerEvents="box-none">
                            {checkpointLayer}
                        </View>
                    ) : (
                        checkpointLayer
                    )}
                </SamSheetBesideContext.Provider>
                {/* Le chat, après Sam : ouvert pendant sa conversation, il se pose sur elle, qui l'attend dessous. */}
                {chatLayer}
            </View>

            {allNotices.map(({ key, visible, ...notice }) => (
                <Snackbar key={key} {...notice} visible={visible && front === key} bottomOffset={aboveFooters} />
            ))}
            <ConfirmDialog
                visible={confirmLeave}
                title={TEXTS.leaveTitle}
                confirmLabel={TEXTS.leaveConfirm}
                cancelLabel={TEXTS.leaveCancel}
                danger
                onConfirm={onConfirmLeave ?? NOOP}
                onCancel={onCancelLeave ?? NOOP}
                onDismiss={onDismissLeave ?? onCancelLeave ?? NOOP}
            />
            <ConfirmDialog
                visible={notice === "session_gone"}
                title={TEXTS.goneTitle}
                message={TEXTS.goneMessage}
                confirmLabel={TEXTS.goneConfirm}
                danger={false}
                hideCancel
                onConfirm={onCloseNotice ?? NOOP}
                onCancel={onCloseNotice ?? NOOP}
            />
            <ConfirmDialog
                visible={notice === "payment" || notice === "forbidden"}
                title={notice === "payment" ? TEXTS.paymentTitle : TEXTS.forbiddenTitle}
                message={noticeMessage ?? (notice === "payment" ? TEXTS.paymentMessage : TEXTS.forbiddenMessage)}
                confirmLabel={TEXTS.goneConfirm}
                danger={false}
                hideCancel
                onConfirm={onCloseNotice ?? NOOP}
                onCancel={onCloseNotice ?? NOOP}
            />
            <ConfirmDialog
                visible={notice === "several_tabs"}
                title={TEXTS.tabsTitle}
                message={TEXTS.tabsMessage}
                confirmLabel={TEXTS.tabsConfirm}
                danger={false}
                hideCancel
                onConfirm={onKeepTab ?? NOOP}
                onCancel={() => {}}
                onDismiss={() => {}}
            />
        </View>
    )
}

/**
 * Une mesure qui rejoint sa cible au rythme de la marque ; en mouvement réduit, aussitôt. `onArrived`
 * dit la cible atteinte (le volet retiré, qui sort alors de la tabulation).
 */
function useEased(to: number, animate: boolean, onArrived?: (to: number) => void, jumpKey?: unknown): Animated.Value {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const value = useRef(new Animated.Value(to)).current
    const arrived = useRef(onArrived)
    arrived.current = onArrived
    // Ce qui se pose plutôt que de glisser : la première mesure du grand écran (`animate` qui s'allume), et
    // un réglage qui arrive (`jumpKey` qui change) — la largeur de l'activité lue sur l'appareil, par exemple.
    const last = useRef({ animate: false, jumpKey })
    useEffect(() => {
        const jump = !last.current.animate || last.current.jumpKey !== jumpKey
        last.current = { animate, jumpKey }
        if (reduceMotion || !animate || jump) {
            value.setValue(to)
            arrived.current?.(to)
            return undefined
        }
        const move = Animated.timing(value, {
            toValue: to,
            duration: theme.motion.standard,
            easing: Easing.bezier(...theme.motion.easing),
            useNativeDriver: false,
        })
        move.start(({ finished }) => {
            if (finished) arrived.current?.(to)
        })
        return () => move.stop()
    }, [to, animate, jumpKey, reduceMotion, value, theme.motion])
    return value
}

/**
 * Le voile de la visio : 1 posé, 0 levé, à la durée `standard` et à la courbe de la marque ; en
 * mouvement réduit, aussitôt. `afterOrbit` : il attend que l'orbite du tandem ait mis Sam devant
 * (`orbitDurationMs` : dans la visio quand elle est à l'écran, sinon dans la bulle) avant de se poser.
 */
function useVeil(on: boolean, afterOrbit: boolean, inStage: boolean): Animated.Value {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const value = useRef(new Animated.Value(on ? 1 : 0)).current
    useEffect(() => {
        if (reduceMotion) {
            value.setValue(on ? 1 : 0)
            return undefined
        }
        const move = Animated.timing(value, {
            toValue: on ? 1 : 0,
            duration: theme.motion.standard,
            delay: on && afterOrbit ? orbitDurationMs(inStage ? "stage" : "bubble", true, theme.motion.entree) : 0,
            easing: Easing.bezier(...theme.motion.easing),
            useNativeDriver: Platform.OS !== "web",
        })
        move.start()
        return () => move.stop()
    }, [on, afterOrbit, inStage, reduceMotion, value, theme.motion])
    return value
}

/** Une vue mesurée à l'écran ; `null` sans vue, ou sans taille. */
function measureBox(view: View | null): Promise<ScreenBox | null> {
    return new Promise((resolve) => {
        if (view == null || typeof view.measureInWindow !== "function") {
            resolve(null)
            return
        }
        view.measureInWindow((x, y, boxWidth, boxHeight) =>
            resolve(boxWidth > 0 && boxHeight > 0 ? { x, y, width: boxWidth, height: boxHeight } : null)
        )
    })
}

/** Tant que `active` : le retour arrière d'Android et Échap sur le web appellent `onEscape`. */
function useEscape(active: boolean, onEscape: () => void) {
    const latest = useRef(onEscape)
    latest.current = onEscape
    useEffect(() => {
        if (!active) return undefined
        return listenEscape(() => latest.current())
    }, [active])
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1 },
    /** Le chat et Sam, par-dessus la page ; retirés pendant la sortie. */
    layers: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
    /** La visio et son voile. */
    stageHost: { flex: 1, minHeight: 0 },
    /** Le voile des feuilles (`scrim`), posé sur la visio quand un volet s'ouvre (`liveLayers`). */
    stageVeil: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.colors.scrim,
    },
    aboveControls: (bottom: number) => ({ position: "absolute", top: 0, left: 0, right: 0, bottom }),
    /** Le gabarit pose une marge haute et basse : ici la scène touche la barre. */
    flush: { paddingVertical: 0 },
    noticeBar: (tint: SemanticColorKey) => ({
        backgroundColor: theme.colors[tint],
        paddingHorizontal: theme.spacing["4"],
        paddingVertical: theme.spacing["2"],
    }),
    noticeText: { flex: 1, flexShrink: 1 },
    /** Le détail s'aligne sous la phrase, après l'icône de la barre. */
    noticeDetail: { paddingTop: theme.spacing["1"], paddingLeft: theme.spacing["6"] },
    empty: {
        flex: 1,
        justifyContent: "center",
        padding: theme.spacing["6"],
    },
    /** Les mesures de `<TwoPaneTemplate>` : au téléphone une vue à la fois, au large deux volets. */
    onePane: { flex: 1, minHeight: 0, flexDirection: "column" },
    /** Une vue du téléphone : elle remplit le volet ; pendant un fondu, l'autre se tient sous elle. */
    view: { position: "absolute", top: 0, right: 0, bottom: 0, left: 0 },
    panes: { flex: 1, minHeight: 0, flexDirection: "row" },
    /** La visio prend la place que l'activité lui laisse, rien quand elle repart (`flexGrow` animé). */
    stagePane: { flexBasis: 0, flexShrink: 1, minWidth: 0, overflow: "hidden" },
    /** La visio qui sort de la bulle ou y rentre déborde de sa colonne, devant l'activité. */
    morphing: { overflow: "visible", zIndex: 1 },
    /** La visio garde sa largeur pendant que sa colonne s'ouvre ou se referme : elle ne se tasse pas. */
    stageSlide: { flex: 1, minHeight: 0 },
    /**
     * L'activité part de la largeur d'un volet (`width` animée, rien quand le partage est agrandi ; pas
     * `flexBasis`, qu'Android ne suit pas sous le pilote JS) et prend ce que la visio lui rend (`flexGrow` animé).
     */
    activityPane: { flexShrink: 0, minWidth: 0 },
    /** Le volet qui se retire ne déborde pas sur la visio. */
    clip: { overflow: "hidden" },
    paneDivider: { borderLeftWidth: 1, borderLeftColor: theme.colors.border },
    gone: { display: "none" },
    /** Posée et mesurée, mais pas vue. */
    unseen: { opacity: 0 },
    /**
     * Le volet retiré par le partage agrandi (déjà à 0 de large), l'activité cachée sous la visio du
     * téléphone : ils sortent aussi de la tabulation et du lecteur d'écran sur le web (`visibility`, comme
     * les onglets de `<LiveDevicesSheet>`).
     */
    unreachable: { _web: { visibility: "hidden" } },
    paneFill: { flex: 1, minWidth: 0 },
}))
